import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { IPetItemResponse } from "../../../../services/pet-list/pet-list.interface";
import { IPrequalificationInfoResponse } from "../../../../services/prequalification";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { PAGES_PATH } from "../../../../navigation/params";
import { PrequalBillingOptionCard } from "./PrequalBillingOptionCard";
import { PrequalResultLayout } from "./PrequalResultLayout";
import React from "react";
import { useMinPlanPrices } from "../../../../hooks/useMinMonthlyPlanPrice";
import { useNavigate } from "react-router-dom";
import { usePrequalContinuation } from "../../../../hooks/usePrequalContinuation";

interface IPrequalAlreadyQualifiedScreenProps {
  record: IPrequalificationInfoResponse;
  pet?: IPetItemResponse;
  userFirstName?: string;
}

// Terminal gate for a non-expired prequalification record. The query only
// ever returns ELIGIBLE or INELIGIBLE here. Adapted to web from the mobile
// mockup (Figma 35456-31979); INELIGIBLE copy is provisional pending product.
export const PrequalAlreadyQualifiedScreen = ({
  record,
  pet,
  userFirstName,
}: IPrequalAlreadyQualifiedScreenProps) => {
  const navigate = useNavigate();
  const { continueFromRecord } = usePrequalContinuation({ source: "prequal_already_qualified" });
  const isEligible = record.status === "ELIGIBLE";
  const expiresAt = record.expiresAt ? new Date(record.expiresAt) : null;
  const petName = pet?.name;
  // Price clause appears once the catalog loads; the copy reads fine without.
  const { monthly: minMonthly, annual: minAnnual } = useMinPlanPrices(pet?.id);
  const monthlyDescription =
    "Cover your pet's annual care with 12 monthly payments" +
    `${minMonthly ? ` starting at $${minMonthly}/month` : ""}.`;
  const annualDescription =
    "Save when you make a one-time annual payment for the full cost of your plan today" +
    `${minAnnual ? `, starting at $${minAnnual} a year` : ""}.`;

  const handleSeeOptions = () => {
    if (pet) {
      // Shared continuation routing (SGPD-475): a delayed return may carry a
      // saved plan selection, which lands directly on Billing Options.
      continueFromRecord(record, pet);
      return;
    }
    navigate(`${PAGES_PATH.WELLNESS_PLAN_DETAILS}?id=${record.petId}`);
  };

  const handleBackToDashboard = () => {
    navigate(PAGES_PATH.DASHBOARD);
  };

  if (!isEligible) {
    return (
      <PrequalResultLayout
        pillLabel="PRE-QUALIFIED"
        title={`You qualify for annual billing${userFirstName ? `, ${userFirstName}` : ""}.`}
        subtitle={
          "The monthly billing option isn't available to you at this time" +
          `${expiresAt ? ` — you can check again after ${expiresAt.toLocaleDateString()}` : ""}. ` +
          "Questions? Contact our support team."
        }
        ctaLabel={petName ? `See ${petName}'s Options` : "See Plan Options"}
        onCta={handleSeeOptions}
        onMaybeLater={handleBackToDashboard}
      >
        <PrequalBillingOptionCard
          title="Annual"
          icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
          description={annualDescription}
          highlighted
        />
      </PrequalResultLayout>
    );
  }

  return (
    <PrequalResultLayout
      pillLabel="PRE-QUALIFIED"
      title={`You're already pre-qualified${userFirstName ? ` ${userFirstName}` : ""}!`}
      subtitle={
        `Both billing options are unlocked${petName ? ` for ${petName}'s TotalCare Plan` : ""}. ` +
        "Pick the one that fits your budget on the next step."
      }
      ctaLabel={petName ? `See ${petName}'s Options` : "See Plan Options"}
      onCta={handleSeeOptions}
      onMaybeLater={handleBackToDashboard}
    >
      <PrequalBillingOptionCard
        title="Monthly"
        icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />}
        description={monthlyDescription}
        badgeLabel="Most Popular"
        showScratchLockup
      />
      <PrequalBillingOptionCard
        title="Annual"
        icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
        description={annualDescription}
      />
    </PrequalResultLayout>
  );
};
